import { FC, useState } from 'react';
import dayjs from 'dayjs';
import { Button, Card, Divider, RadixTable } from '@features/ui';
import {
  periodOptions,
  groupByOptions,
} from '@features/admin/reports/reportData';
import { toast } from 'react-toastify';
import {
  ReportGroupBy,
  ReportPeriod,
  TReportTaxByCustomerCountryData,
  TReportTaxByCustomerCountryParams,
} from '@src/types/reports';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useReportFilters } from '@hooks/useReportFilters';
import { useReportTableColumns } from '@hooks/useReportTableColumns';
import { useReportChartData, getChartColor } from '@hooks/useReportChartData';
import {
  exportTaxByCustomerCountry,
  getTaxByCustomerCountry,
} from '@src/services/reports';
import { downloadFile } from '@src/lib/utils';

import { ReportFilteringSettings } from '../ReportFilteringSettings';

export const TaxByCustomerCountry: FC = () => {
  // Data state
  const [data, setData] = useState<TReportTaxByCustomerCountryData[]>([]);

  // Use custom hooks for filter management
  const filters = useReportFilters({
    defaultPeriod: ReportPeriod.LAST_7_DAYS,
    defaultGroupBy: ReportGroupBy.DAY,
    enableComparisons: true,
  });

  // Use custom hook for table columns generation
  const { columns, displayItems, tableData } =
    useReportTableColumns<TReportTaxByCustomerCountryData>({
      data,
      selectedItems: [],
      compareToPreviousPeriod: filters.compareToPreviousPeriod,
      compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
      getAccessField: (item) => item.date,
      getMainDataField: (item, key) => item.countries[key] || 0,
      getPreviousPeriodField: (item, key) =>
        item.countries_previous?.[key] || 0,
      getPreviousYearField: (item, key) =>
        item.countries_previous_year?.[key] || 0,
      itemsField: 'countries',
      columnTitleFormatter: (key) => key.toUpperCase(),
      valueFormatter: (_, value) => `$${value.toLocaleString()}`,
      includeTotalsRow: true,
    });

  // Use custom hook for chart data transformation
  const chartData = useReportChartData({
    data,
    displayItems,
    compareToPreviousPeriod: filters.compareToPreviousPeriod,
    compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
    getAccessField: (item) => item.date,
    getMainDataField: (item, key) => item.countries[key] || 0,
    getPreviousPeriodField: (item, key) => item.countries_previous?.[key],
    getPreviousYearField: (item, key) => item.countries_previous_year?.[key],
  });

  const handleRunReport = async () => {
    filters.setIsLoading(true);
    try {
      const params: TReportTaxByCustomerCountryParams = {
        start_date: filters.startDate
          ? dayjs(filters.startDate).format('YYYY-MM-DD')
          : null,
        end_date: filters.endDate
          ? dayjs(filters.endDate).format('YYYY-MM-DD')
          : null,
        group: filters.selectedGroupBy.toLowerCase() as ReportGroupBy,
        compare_to_previous_period: filters.compareToPreviousPeriod,
        compare_to_same_period_previous_year:
          filters.compareToSamePeriodPreviousYear,
      };

      const response = await getTaxByCustomerCountry(params);

      setData(response.data);
      toast.success('Report generated successfully!');
    } catch (error) {
      toast.error('Error fetching report');
      console.error('Error fetching report:', error);
    } finally {
      filters.setIsLoading(false);
    }
  };

  const handleExportReport = async (format: 'csv' | 'xlsx') => {
    filters.setIsLoading(true);
    try {
      const payload: TReportTaxByCustomerCountryParams = {
        start_date: filters.startDate
          ? dayjs(filters.startDate).format('YYYY-MM-DD')
          : null,
        end_date: filters.endDate
          ? dayjs(filters.endDate).format('YYYY-MM-DD')
          : null,
        group: filters.selectedGroupBy.toLowerCase() as ReportGroupBy,
        compare_to_previous_period: filters.compareToPreviousPeriod,
        compare_to_same_period_previous_year:
          filters.compareToSamePeriodPreviousYear,
      };

      const response = await exportTaxByCustomerCountry(payload, format);

      if (format === 'csv') {
        const csvBlob = new Blob([response.data], {
          type: 'text/csv;charset=utf-8;',
        });
        const csvUrl = URL.createObjectURL(csvBlob);
        const link = document.createElement('a');

        link.href = csvUrl;
        link.download = 'tax_by_customer_country.csv';
        document.body.appendChild(link);
        link.click();
        URL.revokeObjectURL(csvUrl);
        document.body.removeChild(link);
      } else {
        downloadFile(response.data, 'tax_by_customer_country');
      }

      toast.success('Report exported successfully!');
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report');
    } finally {
      filters.setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">
        Tax By Customer Country Report
      </h1>

      <Card className="p-6">
        <ReportFilteringSettings
          showGroupByFilter
          showPeriodFilter
          compareToPreviousPeriod={filters.compareToPreviousPeriod}
          compareToSamePeriodPreviousYear={
            filters.compareToSamePeriodPreviousYear
          }
          dateRangeValue={filters.valueStart}
          groupByOptions={groupByOptions}
          isLoading={filters.isLoading}
          periodOptions={periodOptions}
          periodRange={filters.periodRange}
          selectedGroupBy={filters.selectedGroupBy}
          selectedPeriod={filters.selectedPeriod}
          showCompareToPreviousPeriod={filters.showPreviousPeriodCheckbox}
          showCompareToSamePeriodPreviousYear={filters.showPreviousYearCheckbox}
          onCompareToPreviousPeriodChange={filters.setCompareToPreviousPeriod}
          onCompareToSamePeriodPreviousYearChange={
            filters.setCompareToSamePeriodPreviousYear
          }
          onDateRangeChange={filters.setValueStart}
          onGroupByChange={filters.setSelectedGroupBy}
          onPeriodChange={filters.setSelectedPeriod}
          onRunReport={handleRunReport}
        />
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <ResponsiveContainer height={400} width="100%">
            <LineChart data={chartData}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                stroke="#999"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#999"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => `$${value.toLocaleString()}`}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1a1a1a',
                  border: '1px solid #3D3D3D',
                  borderRadius: '8px',
                  boxShadow: '0px 7px 13px 0px #B6D72C14',
                }}
                formatter={(value: number) => `$${value.toLocaleString()}`}
              />
              {displayItems.map((country, index) => (
                <Line
                  key={country}
                  activeDot={{ r: 6 }}
                  dataKey={country}
                  dot={{ r: 4 }}
                  stroke={getChartColor(index)}
                  strokeWidth={2}
                  type="monotone"
                />
              ))}
              {filters.compareToPreviousPeriod &&
                displayItems.map((country, index) => (
                  <Line
                    key={`${country}_previous_period`}
                    dataKey={`${country}_previous_period`}
                    dot={{ r: 3 }}
                    stroke={getChartColor(index)}
                    strokeDasharray="5 5"
                    strokeWidth={2}
                    type="monotone"
                  />
                ))}
              {filters.compareToSamePeriodPreviousYear &&
                displayItems.map((country, index) => (
                  <Line
                    key={`${country}_previous_year`}
                    dataKey={`${country}_previous_year`}
                    dot={{ r: 3 }}
                    stroke={getChartColor(index)}
                    strokeDasharray="2 2"
                    strokeWidth={2}
                    type="monotone"
                  />
                ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-white">Detailed Data</h2>
          <RadixTable columns={columns} data={tableData} />
          <Divider className="my-4" />
          <div className="flex justify-end">
            <Button
              className="text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
              variant="outline"
              onClick={() => handleExportReport('csv')}
            >
              Download CSV
            </Button>
            <Button
              className="ml-2 text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
              variant="outline"
              onClick={() => handleExportReport('xlsx')}
            >
              Download XLSX
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};
